/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import {
  primitiveNumberTypes,
  getValueDataArray, UtrValueTypes
} from './universalTracker';
import { Button, Popover, PopoverBody, PopoverHeader } from 'reactstrap';
import { SimpleTooltip } from '../components/simple-tooltip';
import { NotApplicableTypes, UtrvStatus } from '../constants/status';
import {
  renderNumericValue,
  conversionParams,
  getUnitCode,
  getNumberScale,
  isNaOrNr,
  getUnitValue,
  getListValueDataData,
} from './universalTrackerValue';
import TableInputView from '../components/survey/form/view/TableInputView';
import { tableDataToView } from './valueDataTable';
import UniversalTracker from '../model/UniversalTracker';
import { UniversalTrackerValuePlain } from '../types/surveyScope';
import { ValueData } from '../components/survey/question/questionInterfaces';
import { SurveyModelMinimalUtrv } from '../model/surveyData';

export type MinimalUtrv = Pick<SurveyModelMinimalUtrv, '_id' | 'valueData' | 'status' | 'value'>

interface UniversalTrackerValueViewProps {
  utr: UniversalTracker;
  utrv: MinimalUtrv;
  valueType: string;
  className?: string;
  showTableDownload?: boolean;
  extractColumnCode?: string;
}

interface ValueFiltered {
  extractColumnCode: string | undefined,
  valueType: string,
  utrv: MinimalUtrv,
  unitValue: number | undefined
}

const getValueFiltered = ({ extractColumnCode, valueType, utrv, unitValue }: ValueFiltered): string | number | undefined => {
  if (extractColumnCode && valueType === UtrValueTypes.numericValueList) {
    const data = getListValueDataData(utrv);
    if (typeof data === 'object' && !Array.isArray(data)) {
      return data[extractColumnCode];
    }
    return undefined
  }

  return unitValue;
};

export const UniversalTrackerValueView = (props: UniversalTrackerValueViewProps) => {
  const { utr, utrv, valueType, className = 'text-ThemeAccentMedium', showTableDownload, extractColumnCode } = props;

  const [showDetailPopup, setShowDetailsPopup] = React.useState(false);

  const unit = getUnitCode(utrv, utr);
  const numberScale = getNumberScale(utrv, utr);
  const unitValue = getUnitValue(utrv);

  // NA/NR
  const { valueData } = utrv;
  if (isNaOrNr(utrv)) {
    return <span className='badge bg-warning'>
      {valueData?.notApplicableType === NotApplicableTypes.nr ? 'N/R' : 'N/A'}
    </span>;
  }

  if (utrv.status === UtrvStatus.Created) {
    return null;
  }

  // Simple Values or numericaValueList gets a total in utrv.value so we can show that
  const valueTypeHasValue = primitiveNumberTypes.includes(valueType) || valueType === UtrValueTypes.numericValueList;
  if (valueTypeHasValue) {
    if (unitValue === undefined) {
      return null;
    }
    const valueFiltered = getValueFiltered({extractColumnCode, valueType, utrv, unitValue})
    return renderNumericValue(conversionParams(utrv, utr, {
      value: valueFiltered,
      unit,
      numberScale
    }), className, false);
  }

  if (!valueData) {
    return null;
  }

  if (valueType === UtrValueTypes.valueList) {
    const text = utr.getValueListData(valueData.data);
    return <div className={'text-center text-truncate'}>
      <SimpleTooltip placement={'auto'} text={text}>
        {text}
      </SimpleTooltip>
    </div>
  }

  const values = getValueDataArray({
    utr: utr,
    data: valueData.data,
    value: unitValue,
    unit: unit,
    numberScale: getNumberScale(utrv, utr),
    displayCheckbox: undefined
  });

  if (valueType === UtrValueTypes.table && !valueData.table?.length) {
    return null;
  }

  const complexValue = getComplexText({
    values,
    valueType,
    valueData,
    utr,
    utrv,
    showTableDownload,
    extractColumnCode
  });

  if (!complexValue) {
    return null;
  }

  if (extractColumnCode) {
    return complexValue;
  }

  return (
    <div className='text-center'>
      <Button
        id={`popover-${utrv._id}`}
        color='link'
        type='button'
        onMouseEnter={() => setShowDetailsPopup(true)}
        onMouseLeave={() => setShowDetailsPopup(false)}
      >
        <i className='fa fa-info-circle muted' />
      </Button>
      {showDetailPopup ? (
        <Popover
          placement='bottom'
          target={`popover-${utrv._id}`}
          isOpen={true}
          toggle={() => setShowDetailsPopup(!showDetailPopup)}
          className={'renderValueTableTooltip'}
          onClick={() => setShowDetailsPopup(!showDetailPopup)}
        >
          <PopoverHeader>{utr.getName()}</PopoverHeader>
          <PopoverBody onMouseEnter={() => setShowDetailsPopup(true)} onMouseLeave={() => setShowDetailsPopup(false)}>
            {complexValue}
          </PopoverBody>
        </Popover>
      ) : null}
    </div>
  );
}

interface RenderComplexTextProps {
  values: {
    label?: string;
    value: undefined | string | number;
    unit: string;
    numberScale?: string;
  }[];
  valueType: string;
  valueData: ValueData;
  utr: UniversalTracker;
  utrv: Pick<UniversalTrackerValuePlain, 'value'>;
  showTableDownload?: boolean;
  extractColumnCode?: string;
}

const getComplexText = (p: RenderComplexTextProps) => {
  const {
    values,
    valueType,
    valueData,
    utr,
    utrv,
    showTableDownload,
    extractColumnCode
  } = p;

  if (!valueData) {
    return null;
  }
  switch (valueType) {
    case 'text':
      if (!valueData.data) {
        return null;
      }
      return <ul className='list-group text-left'>
        <li className='list-group-item'>{valueData.data}</li>
      </ul>
    case 'valueList':
    case 'valueListMulti':
      if (!values) {
        return null;
      }
      return <ul className='list-group text-left'>
        {values.map((item) => <li key={item.label}
          className='list-group-item'>{item.value}</li>)}
      </ul>
    case 'textValueList':
      if (!values) {
        return null;
      }
      return <ul className='list-group text-left inline-values'>
        {values.map((item) => {
          const { label, value } = item;
          return <li key={label} className='list-group-item'>
            {utr.getValueListData(label)}: <br /> <span
              className='strong text-primary'>{value}</span>
          </li>;
        })}
      </ul>
    case 'numericValueList':
      if (!values) {
        return null;
      }
      return <ul className='list-group text-left inline-values'>
        {values.map((item) => {
          const { label } = item;
          return <li key={label} className='list-group-item'>
            {utr.getValueListData(label)}: <br /> {renderNumericValue(conversionParams(utrv, utr, item))}
          </li>;
        })}
      </ul>
    case 'table':
      if (!valueData.table?.length) {
        return null;
      }
      const { rows, editRowId } = tableDataToView(valueData);
      return <TableInputView
        universalTracker={utr}
        disabled={true}
        editRowId={editRowId}
        rowData={rows}
        showTableDownload={showTableDownload}
        extractColumnCode={extractColumnCode}
      />
    case 'date':
      if (!valueData.data) {
        return null;
      }
      return <span className='strong text-primary'>{valueData.data}</span>
    default:
      if (!values) {
        return null;
      }
      return <ul className='list-group text-left inline-values'>
        {
          values.map(({ value, unit, numberScale }, i) => {
            return <li key={i} className='list-group-item'>
              {renderNumericValue(
                conversionParams(utrv, utr, {
                  value,
                  unit,
                  numberScale
                })
              )}
            </li>;
          })
        }
      </ul>
  }
}
